import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import dayjs from 'dayjs';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import fetchData from '../../helpers/fetchData';
import TableWidget, { HeaderMappingItem } from '../../helpers/tableBuilder';
import { formatGerman } from '../../helpers/chartUtils';
import { tokens } from '../../theme';
import CoregMatrixWidget from './CoregMatrixWidget';
import CoregTimelineChart, { CoregTimelinePoint } from './CoregTimelineChart';
import CoregErrorsModal from './CoregErrorsModal';

type CoregUnit = 'process' | 'email_day';

const PORTAL_LABELS: Record<string, string> = {
  'trading-treff.de': 'Trading-Treff',
  'stock-world.de': 'Stock-World',
  'boerse-global.de': 'Börse Global',
  'boerse-express.com': 'Börse Express',
  'kapitalmarktexperten.de': 'Kapitalmarkt-Experten',
};

const ALL_PORTALS = Object.keys(PORTAL_LABELS);

interface CoregSummary {
  total: number;
  both_new: number;
  mc_new_vnr_dup: number;
  mc_dup_vnr_new: number;
  both_dup: number;
  incomplete: number;
}

interface BreakdownRow extends CoregSummary {
  origin_host: string;
  portal_label: string;
  analyzable: number;
  wasted_pct: number | null;
  fully_new_pct: number | null;
}

const breakdownHeaderMapping: Record<string, HeaderMappingItem> = {
  portal_label: { name: 'Portal', type: 'string' },
  total: { name: 'Gesamt', type: 'number' },
  both_new: { name: 'Beide neu', type: 'number' },
  mc_dup_vnr_new: { name: 'Nur MC-Dup.', type: 'number' },
  mc_new_vnr_dup: { name: 'Nur VNR-Dup.', type: 'number' },
  both_dup: { name: 'Beide Dup.', type: 'number' },
  incomplete: { name: 'Unvollst./Fehler', type: 'number' },
  wasted_pct: { name: '% verschwendet*', type: 'percentage', digits: 2 },
  fully_new_pct: { name: '% voll verwertet*', type: 'percentage', digits: 2 },
};

const toPercent = (part: number, analyzable: number): number | null => {
  if (analyzable <= 0) return null;
  return part / analyzable;
};

const CoregEfficiencyPage: React.FC = () => {
  const { filterState } = useContext(FilterContext)!;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [selectedPortals, setSelectedPortals] = useState<string[]>(ALL_PORTALS);
  const [unit, setUnit] = useState<CoregUnit>('process');
  const [viewMode, setViewMode] = useState<'absolute' | 'relative'>('absolute');

  const [summary, setSummary] = useState<CoregSummary | null>(null);
  const [timeline, setTimeline] = useState<CoregTimelinePoint[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [detailsModal, setDetailsModal] = useState<{
    open: boolean;
    portalLabel: string;
    portalValue: string;
  }>({ open: false, portalLabel: '', portalValue: '' });

  const queryParams = useMemo(() => {
    if (!filterState.startDate || !filterState.endDate) return null;
    if (selectedPortals.length === 0) return null;
    return {
      startDate: dayjs(filterState.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(filterState.endDate).format('YYYY-MM-DD'),
      portals: selectedPortals.join(','),
      unit,
    };
  }, [filterState.startDate, filterState.endDate, selectedPortals, unit]);

  useEffect(() => {
    if (!queryParams) {
      setSummary(null);
      setTimeline([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingSummary(true);
      try {
        const [summaryResp, timelineResp] = await Promise.all([
          fetchData<CoregSummary>(
            '/api/coreg-efficiency/summary',
            queryParams,
            false,
          ) as Promise<CoregSummary>,
          fetchData<CoregTimelinePoint>('/api/coreg-efficiency/timeline', queryParams) as Promise<
            CoregTimelinePoint[]
          >,
        ]);
        if (cancelled) return;
        setSummary(summaryResp);
        setTimeline(Array.isArray(timelineResp) ? timelineResp : []);
      } catch (err) {
        console.error('Error loading co-reg efficiency data:', err);
        if (!cancelled) {
          setSummary(null);
          setTimeline([]);
        }
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [queryParams]);

  const fetchBreakdown = useCallback(
    async (_filterState: IFilterState): Promise<BreakdownRow[]> => {
      if (!queryParams) return [];
      const rows = (await fetchData<any>('/api/coreg-efficiency/breakdown', queryParams)) as any[];
      if (!Array.isArray(rows)) return [];
      return rows.map((row) => {
        const analyzable =
          Number(row.both_new) +
          Number(row.mc_new_vnr_dup) +
          Number(row.mc_dup_vnr_new) +
          Number(row.both_dup);
        return {
          origin_host: String(row.origin_host),
          portal_label: PORTAL_LABELS[row.origin_host] ?? String(row.origin_host),
          total: Number(row.total) || 0,
          both_new: Number(row.both_new) || 0,
          mc_new_vnr_dup: Number(row.mc_new_vnr_dup) || 0,
          mc_dup_vnr_new: Number(row.mc_dup_vnr_new) || 0,
          both_dup: Number(row.both_dup) || 0,
          incomplete: Number(row.incomplete) || 0,
          analyzable,
          wasted_pct: toPercent(Number(row.both_dup) || 0, analyzable),
          fully_new_pct: toPercent(Number(row.both_new) || 0, analyzable),
        };
      });
    },
    [queryParams],
  );

  const computeBreakdownSummary = (data: BreakdownRow[]): BreakdownRow => {
    const sum: BreakdownRow = {
      origin_host: '',
      portal_label: 'Insgesamt',
      total: 0,
      both_new: 0,
      mc_new_vnr_dup: 0,
      mc_dup_vnr_new: 0,
      both_dup: 0,
      incomplete: 0,
      analyzable: 0,
      wasted_pct: null,
      fully_new_pct: null,
    };
    data.forEach((row) => {
      sum.total += row.total;
      sum.both_new += row.both_new;
      sum.mc_new_vnr_dup += row.mc_new_vnr_dup;
      sum.mc_dup_vnr_new += row.mc_dup_vnr_new;
      sum.both_dup += row.both_dup;
      sum.incomplete += row.incomplete;
      sum.analyzable += row.analyzable;
    });
    sum.wasted_pct = toPercent(sum.both_dup, sum.analyzable);
    sum.fully_new_pct = toPercent(sum.both_new, sum.analyzable);
    return sum;
  };

  const handlePortalChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedPortals(typeof value === 'string' ? value.split(',') : value);
  };

  const analyzable = summary
    ? summary.both_new + summary.mc_new_vnr_dup + summary.mc_dup_vnr_new + summary.both_dup
    : 0;

  const kpis = summary
    ? [
        {
          label: 'Gesamt',
          value: formatGerman.format(summary.total),
          sub: `${formatGerman.format(analyzable)} auswertbar`,
          color: colors.blueAccent[600],
        },
        {
          label: 'Beide neu',
          value:
            analyzable > 0
              ? `${((100 * summary.both_new) / analyzable).toLocaleString('de-DE', {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })} %`
              : '—',
          sub: `${formatGerman.format(summary.both_new)} Leads`,
          color: colors.greenAccent[700],
        },
        {
          label: 'Nur halb verwertet',
          value:
            analyzable > 0
              ? `${(
                  (100 * (summary.mc_new_vnr_dup + summary.mc_dup_vnr_new)) / analyzable
                ).toLocaleString('de-DE', {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })} %`
              : '—',
          sub: `${formatGerman.format(summary.mc_new_vnr_dup + summary.mc_dup_vnr_new)} Leads`,
          color: '#b08900',
        },
        {
          label: 'Beide Dublette (verschwendet)',
          value:
            analyzable > 0
              ? `${((100 * summary.both_dup) / analyzable).toLocaleString('de-DE', {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })} %`
              : '—',
          sub: `${formatGerman.format(summary.both_dup)} Leads`,
          color: colors.redAccent[700],
        },
      ]
    : [];

  const startDate = filterState.startDate ?? dayjs().subtract(3, 'month').toDate();
  const endDate = filterState.endDate ?? new Date();

  return (
    <Box m="20px">
      <Typography variant="h4" fontWeight="bold" mb={1}>
        Co-Reg Effizienz Report
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        {
          'Wie oft war ein bestätigter Co-Reg-Lead bereits auf beiden Zielsystemen (Mailchimp-Liste des Portals + VNR-GeVestor/Investor)? Beide APIs müssen „bereits eingetragen" melden, damit ein Lead als verschwendet zählt. Prozentzahlen* beziehen sich auf auswertbare Leads (ohne Fehler/Unvollständig).'
        }
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={3}>
        {
          'Mailchimp-Codes: 100 = neu, 101 = Dublette, 102 = Dublette + Tag-Update (zählt als Dublette), 103 = Reaktiviert/Inaktiv-Tag entfernt (zählt als neu). VNR/NSS: 200 = neu, 201 = Dublette. Klick auf eine Portal-Zeile unten zeigt die Detail-Codes.'
        }
      </Typography>

      <Paper sx={{ p: 2, mb: 3, borderRadius: '10px' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="portal-filter-label">Portale</InputLabel>
              <Select
                labelId="portal-filter-label"
                multiple
                value={selectedPortals}
                onChange={handlePortalChange}
                input={<OutlinedInput label="Portale" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} size="small" label={PORTAL_LABELS[value] ?? value} />
                    ))}
                  </Box>
                )}
              >
                {ALL_PORTALS.map((portal) => (
                  <MenuItem key={portal} value={portal}>
                    {PORTAL_LABELS[portal]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" gap={2} alignItems="center">
              <Typography variant="body2">Einheit:</Typography>
              <ToggleButtonGroup
                value={unit}
                exclusive
                size="small"
                onChange={(_e, value) => value && setUnit(value)}
              >
                <ToggleButton value="process">Pro Lead-Einreichung</ToggleButton>
                <Tooltip title="Gruppiert identische E-Mails am selben Tag pro Portal zu einem Lead. MIN(result_code) bestimmt das Ergebnis.">
                  <ToggleButton value="email_day">Pro E-Mail · Tag</ToggleButton>
                </Tooltip>
              </ToggleButtonGroup>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} mb={3}>
        {kpis.map((kpi) => (
          <Grid item xs={12} sm={6} md={3} key={kpi.label}>
            <Paper
              sx={{
                p: 2,
                backgroundColor: kpi.color,
                color: '#fff',
                borderRadius: '10px',
                minHeight: 110,
              }}
            >
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                {kpi.label}
              </Typography>
              <Typography variant="h3" fontWeight="bold" sx={{ my: 0.5 }}>
                {kpi.value}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {kpi.sub}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          {summary && (
            <CoregMatrixWidget
              counts={{
                both_new: summary.both_new,
                mc_new_vnr_dup: summary.mc_new_vnr_dup,
                mc_dup_vnr_new: summary.mc_dup_vnr_new,
                both_dup: summary.both_dup,
              }}
            />
          )}
        </Grid>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 2, borderRadius: '10px', backgroundColor: colors.primary[400] }}>
            <Typography variant="h6" fontWeight="bold" mb={1}>
              Zeitverlauf
            </Typography>
            <CoregTimelineChart
              data={timeline}
              startDate={startDate}
              endDate={endDate}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
          </Paper>
        </Grid>
      </Grid>

      <Box mt={3}>
        <Paper sx={{ p: 2, borderRadius: '10px', backgroundColor: colors.primary[400] }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Aufschlüsselung nach Portal
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Klick auf eine Zeile öffnet die Fehler/Unklar-Details für dieses Portal.
          </Typography>
          <TableWidget<BreakdownRow>
            key={`breakdown-${selectedPortals.join(',')}-${unit}-${queryParams?.startDate}-${queryParams?.endDate}`}
            getData={fetchBreakdown}
            detailsHeaderMapping={breakdownHeaderMapping}
            initialSortColumn="total"
            sortOrder="desc"
            excelDownloadName="coreg_efficiency_breakdown"
            computeSummary={computeBreakdownSummary}
            onRowClick={(row) => {
              if (!queryParams) return;
              const isSummary = !row.origin_host;
              setDetailsModal({
                open: true,
                portalLabel: isSummary ? 'Alle ausgewählten Portale' : row.portal_label,
                portalValue: isSummary ? selectedPortals.join(',') : row.origin_host,
              });
            }}
          />
        </Paper>
      </Box>

      <CoregErrorsModal
        open={detailsModal.open}
        onClose={() => setDetailsModal((prev) => ({ ...prev, open: false }))}
        portalLabel={detailsModal.portalLabel}
        queryParams={
          queryParams && detailsModal.portalValue
            ? {
                startDate: queryParams.startDate,
                endDate: queryParams.endDate,
                portals: detailsModal.portalValue,
              }
            : null
        }
      />
    </Box>
  );
};

export default CoregEfficiencyPage;
